import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ImageModel } from '../../models/image';
import { ImagesManagerService } from '../../../data/images/images-manager.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { ShiftMapDataService } from '../../../pages/shift-map/services/shift-map-data.service';
import { HighlightedItemSource } from '../../models/highlighted-item-update';
import { HttpClient } from '@angular/common/http';
import { HttpErrorHandler } from '../../../http.error.handler';
import { catchError, retry } from 'rxjs/operators';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DatePipe } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'app-image-viewer',
  templateUrl: './image-viewer.component.html',
  styleUrls: ['./image-viewer.component.scss'],
  imports: [MatProgressSpinner, MatIconButton, DatePipe, MatIcon, MatTooltip],
  providers: [
    DatePipe,
  ],
})
export class ImageViewerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() images: ImageModel[];
  @Input() isLiveMap = true;

  imageData: SafeResourceUrl;
  imageUrl: string;
  loadingImage = false;
  imageError = false;

  selectedItem: ImageModel = null;
  selectedItemIndex: number = null;
  positionString: string;

  highlightedImageSubscription: Subscription;

  constructor(
    private readonly datePipe: DatePipe,
    private imageManager: ImagesManagerService,
    private shiftMapManager: ShiftMapDataService,
    private http: HttpClient,
    private domSanitizer: DomSanitizer
  ) {}

  ngOnInit() {
    if (this.selectedItem === null && !!this.images && this.images.length > 0) {
      this.selectedItemIndex = 0;
      this.updatePosition();
      this.selectedItem = this.images[this.selectedItemIndex];
    }
    this.loadImage();

    if (this.isLiveMap) {
      this.highlightedImageSubscription = this.imageManager.highlightedImage$.subscribe(imageUpdate => {
        if (!!imageUpdate.itemId && imageUpdate.source === HighlightedItemSource.MAP) {
          this.selectedItemIndex = this.images.findIndex(image => image.id === imageUpdate.itemId);
          this.updatePosition();
          this.selectedItem = this.images[this.selectedItemIndex];
          this.loadImage();
        }
      });
    } else {
      this.highlightedImageSubscription = this.shiftMapManager.highlightedImage$.subscribe(highlightedImageUpdate => {
        if (!!highlightedImageUpdate.itemId && highlightedImageUpdate.source === HighlightedItemSource.MAP) {
          this.selectedItemIndex = this.images.findIndex(image => image.id === highlightedImageUpdate.itemId);
          this.updatePosition();
          this.selectedItem = this.images[this.selectedItemIndex];
          this.loadImage();
        }
      });
    }
  }

  ngOnDestroy() {
    this.highlightedImageSubscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    // if already initialized
    if (
      (this.selectedItem === null || (!!changes['images'].currentValue && !!changes['images'].previousValue)) &&
      !!this.images &&
      this.images.length > 0
    ) {
      this.selectedItemIndex = 0;
      this.updatePosition();
      this.selectedItem = this.images[this.selectedItemIndex];
    }
    this.loadImage();
  }

  private loadImage() {
    this.imageError = false;
    this.loadingImage = true;
    // this.imageData = undefined; // avoid white flashing when changing images
    // I cannot use authorization as request param because of authorization lambda response caching
    // this.imageData = this.selectedItem.imageUrl + `?Authorization=${this.token}`;
    firstValueFrom(
      this.http.get(this.selectedItem.imageUrl, { responseType: 'blob' }).pipe(
        retry(0),
        catchError(HttpErrorHandler.handleError) // then handle the error
      )
    )
      .then(response => {
        this.imageUrl = URL.createObjectURL(response);
        this.imageData = this.domSanitizer.bypassSecurityTrustResourceUrl(this.imageUrl);
      })
      .catch(error => {
        this.onImageError();
      });
  }

  onImageError() {
    // console.log('on error');
    this.imageError = true;
    this.loadingImage = false;
  }

  onImageLoad() {
    // console.log('on load');
    this.imageError = false;
    this.loadingImage = false;
  }

  openImageInNewTab(): void {
    // Extract the blob URL from the SafeResourceUrl
    const url = (this.imageData as any).changingThisBreaksApplicationSecurity;
    window.open(url, '_blank');
  }

  downloadImage(): void {
    // Create a temporary anchor element
    const url = this.imageUrl;
    const link = document.createElement('a');
    link.href = url;

    const formattedDate = this.datePipe.transform(this.selectedItem.time, 'yyyyMMdd-HHmmss');
    link.download = !this.selectedItem ? 'dashcam-image.jpg' : `dashcam-image-${this.selectedItem.id}_${formattedDate}`;

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  previousImage() {
    const newIndex = this.selectedItemIndex - 1;
    if (newIndex < 0) {
      this.selectedItemIndex = this.images.length - 1;
    } else {
      this.selectedItemIndex = newIndex;
    }
    this.updatePosition();
    this.selectedItem = this.images[this.selectedItemIndex];
    this.highlightImageOnMap(this.selectedItem.id);
    this.loadImage();
  }

  nextImage() {
    const newIndex = this.selectedItemIndex + 1;
    if (newIndex > this.images.length - 1) {
      this.selectedItemIndex = 0;
    } else {
      this.selectedItemIndex = newIndex;
    }
    this.updatePosition();
    this.selectedItem = this.images[this.selectedItemIndex];
    this.highlightImageOnMap(this.selectedItem.id);
    this.loadImage();
  }

  private updatePosition() {
    this.positionString = `${this.selectedItemIndex + 1} / ${this.images.length}`;
  }

  private highlightImageOnMap(imageId: number) {
    if (this.isLiveMap) {
      this.imageManager.highlightImage(imageId, HighlightedItemSource.PANEL);
    } else {
      this.shiftMapManager.sendHighlightedImageUpdate(imageId, HighlightedItemSource.PANEL);
    }
  }
}
